import { ReactNode, useState } from "react";
import Modal from "../../components/modal/Modal";
import { EHR, RequestStatus, WaitlistRequest } from "../../types";
import styles from "./WaitlistRequests.module.scss";
import { renderMap } from "./waitlist-requests.constants";
import Button from "../../components/button/Button";
import { RootState, useAppDispatch } from "../../store";
import { useSelector } from "react-redux";
import { updateWaitlistRequest } from "../../slices/WaitlistRequestsSlice";
import { createPatientData } from "../../services/webptPatientService";
import { getAssignedProvider, getCaseStudies } from "../../services/webptPatientService";
import { convertToCallablePhoneNumber } from "../../utils/utils";
import { createPatient } from "../../slices/PatientSlice";
import { useSnackbar } from "../../providers/SnackbarProvider";

const excludedKeyMap: Record<string, boolean> = {};

interface RequestViewModalProps {
  visible: boolean;
  onClose: () => void;
  request: WaitlistRequest | null;
}

const RequestViewModal = ({ visible, onClose, request }: RequestViewModalProps) => {
  const [isApprovingRequest, setIsApprovingRequest] = useState(false);
  const [isDecliningRequest, setIsDecliningRequest] = useState(false);
  const webptPatients = Object.values(useSelector((state: RootState) => state.webptIntegration.patientMap)); // TODO: THIS IS BROKEN
  const user = useSelector((state: RootState) => state.auth.user);
  const superOrganization = useSelector((state: RootState) => state.auth.superOrganization);
  const agent = useSelector((state: RootState) => state.agents.agents[0]);
  const dispatch = useAppDispatch();

  const { showMessage } = useSnackbar();

  const renderValue = (value: string, recursionIndex: number) => {
    const handleObjectRender = (obj: any) =>
      Array.isArray(obj) && typeof obj?.[0] === "object"
        ? obj.map((item) => displayObjectProps(item, recursionIndex + 1))
        : displayObjectProps(obj, recursionIndex + 1);
    return typeof value === "object" ? handleObjectRender(value) : String(value);
  };

  const displayObjectProps = (record: Record<string, any>, recursionIndex = 0) => {
    return Object.entries(record || {}).map(([key, value], index) => {
      if (excludedKeyMap[key]) {
        return null;
      }
      return (
        <span
          className={`flex ${typeof value === "object" ? "column" : "flex"} margin-left-${recursionIndex} margin-top${index !== 0 ? "" : "-compact"}`}
          key={index}
        >
          <span className={[styles["details-key"], styles[`wide-${recursionIndex}`]].join(" ")}>
            {key.replace(/([A-Z])/g, " $1").trim()}
            {Array.isArray(value) ? ` (${value.length})` : ""}:{" "}
          </span>
          <strong>{renderMap[key] !== undefined ? renderMap[key](value) : renderValue(value, recursionIndex)}</strong>
        </span>
      );
    });
  };

  const handleApproveRequest = async () => {
    setIsApprovingRequest(true);
    if (request?.ehr === EHR.webpt) {
      const patient = webptPatients.find((p) => p.webptPatientId.toString() === request?.patientId.toString());

      let caseStudy: number | undefined = undefined;
      let phoneNumber: string = "Unknown Phone Number";
      let preferredName: string | undefined = undefined;
      if (patient && user?.token && user?.organizationId) {
        const providers = await getAssignedProvider(patient.webptPatientId, user?.token, user?.organizationId);
        const studies = await getCaseStudies(patient?.webptPatientId, user?.token, user?.organizationId, patient?.firstName, patient?.lastName);
        if (studies.length > 0) {
          caseStudy = studies[0].CaseID;
          phoneNumber = studies[0].phone ? convertToCallablePhoneNumber(studies[0].phone) : "Unknown Phone Number";
          const study = studies[0];
          if (!superOrganization?.ignorePreferredName) {
            preferredName = study?.Title.includes('"') ? study.Title.split('"')[1] : undefined;
          }
        }

        try {
          const omniPatient = createPatientData({
            token: user?.token,
            organizationId: user?.organizationId,
            selectedWebptPatient: patient,
            selectedCaseStudy: caseStudy,
            selectedProviders: providers,
            duration: agent?.defaultAppointmentDuration || 30,
            phoneNumber: phoneNumber,
            preferredName: preferredName,
          });

          const resultAction = await dispatch(
            createPatient({
              token: user.token,
              omniPatient,
            })
          );

          if (createPatient.rejected.match(resultAction)) {
            throw new Error(resultAction.payload?.message || "Failed to create patient");
          }

          if (request) {
            dispatch(
              updateWaitlistRequest({
                requestId: request?.requestId,
                waitlistRequestStatus: RequestStatus.APPROVED,
              })
            );
          }

          onClose();
        } catch (error: any) {
          console.error("Error creating patient:", error);
          showMessage("Error creating patient: " + error.message, "error");
        }
      }
    }

    setIsApprovingRequest(false);
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <div className={styles["request-view-modal"]}>
        <h1 className={styles["request-view-modal-title"]}>Request Details</h1>
        {request && <div className={styles["request-details"]}>{displayObjectProps(request)}</div>}

        {request?.status === RequestStatus.PENDING && (
          <div className={styles["request-view-modal-actions"]}>
            <Button
              type="bordered"
              onClick={() => {
                setIsDecliningRequest(true);
                dispatch(
                  updateWaitlistRequest({
                    requestId: request?.requestId,
                    waitlistRequestStatus: RequestStatus.DENIED,
                  })
                );
                onClose();
                setIsDecliningRequest(false);
              }}
              danger
              loading={isDecliningRequest}
            >
              Decline Request
            </Button>
            <Button type="primary" onClick={handleApproveRequest} loading={isApprovingRequest}>
              Approve Request
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RequestViewModal;
